<template>
    <GridComp
        ref="grid"
        :grid-setup="gridSetup"
    />
</template>

<script setup lang="ts">
import { DisplayTextWithRoute, DisplayTimespan, GridComp, ValidatedInt } from '@/components';
import { GridColumnDefinition, GridColumnOptions, GridDataSource, GridSetup } from '@/components/Grid/Classes';
import type { StationDeviceStatus, StationDeviceStatusWithCount } from '@/objects/generated';
import IsConnectedIcon from './IsConnectedIcon.vue';
import { $tm } from '@/locize';
import BatteryStatus from './BatteryStatus.vue';
import AntennaStatus from './AntennaStatus.vue';
import DateComp from '@/components/Basic/DateComp.vue';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { computed, ref, watch } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import ValidatedText from '@/components/Basic/ValidatedText.vue';
import DeviceStatusDisplayEvent from './DeviceStatusDisplayEvent.vue';
import DeviceStatusMessages from './DeviceStatusMessages.vue';
import DeviceDetailIcon from './DeviceDetailIcon.vue';
import type { TimeSpan } from '@/objects/TimeSpan';
import DeviceReceiveOffset from './DeviceReceiveOffset.vue';

const props = withDefaults(
    defineProps<{
        rows: StationDeviceStatus[] | StationDeviceStatusWithCount[];
        /**
         * If we should show the event column
         * @default false
         */
        eventcolumn?: boolean;

        dataType?: 'StationDeviceStatus' | 'StationDeviceStatusWithCount';
    }>(),
    {
        eventcolumn: false,

        dataType: 'StationDeviceStatus',
    },
);

const grid = ref<ComponentExposed<typeof GridComp<StationDeviceStatus>> | null>(null);

watch(
    () => props.rows,
    (nv, ov) => {
        if (nv.length != ov.length) {
            grid?.value?.purgeCachedData();
        }
    },
);

/**
 * Define all the colums for the grid
 */
const columns = computed<GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>[]>(() => {
    let _columns: GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>[] = [];

    if (props.dataType == 'StationDeviceStatusWithCount') {
        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'StationAlias.Alias',
                columnHeaderText: '',
                editable: false,
                sortable: false,
                filterable: false,
                resizable: false,
                minWidth: 40,
                maxWidth: 40,
                cellRenderer: DeviceDetailIcon,
            }),
        );
    }

    if (props.eventcolumn) {
        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'Station.EventName',
                columnHeaderText: $tm('general.eventname'),
                editable: false,
                sortable: true,
                filterable: false,
                minWidth: 150,
                cellRenderer: DeviceStatusDisplayEvent,
            }),
        );
    }

    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'Station.Name',
            columnHeaderText: $tm('general.stationname'),
            editable: false,
            sortable: true,
            filterable: false,
            minWidth: 60,
            cellRenderer: ValidatedText,
        }),
    );

    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'DeviceStatus.Connected',
            cellDataType: 'boolean',
            columnHeaderText: $tm('general.isconnected'),
            editable: false,
            sortable: true,
            resizable: true,
            cellRenderer: IsConnectedIcon,
            valueGetter: (row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                return row.DeviceStatus == null ? null : row.DeviceStatus!.Connected;
            },
        }),
    );

    // Add a details field
    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'StationAlias.Alias',
            cellDataType: 'text',
            columnHeaderText: $tm('general.stationalias'),
            editable: false,
            sortable: true,
            resizable: true,
            minWidth: 60,
            maxWidth: 170,
            cellRenderer: ValidatedText,
        }),
    );

    if (props.dataType == 'StationDeviceStatusWithCount') {
        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'TotalCount',
                cellDataType: 'number',
                columnHeaderText: $tm('general.totalmessages'),
                editable: false,
                sortable: true,
                cellRenderer: ValidatedInt,
            }),
        );

        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'TimeCount',
                cellDataType: 'number',
                columnHeaderText: $tm('general.timecount'),
                editable: false,
                sortable: true,
                cellRenderer: ValidatedInt,
            }),
        );

        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'StatusCount',
                cellDataType: 'number',
                columnHeaderText: $tm('general.statuscount'),
                editable: false,
                sortable: true,
                cellRenderer: ValidatedInt,
            }),
        );
    }

    if (props.dataType == 'StationDeviceStatus') {
        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'DeviceStatus.DeviceType',
                cellDataType: 'text',
                columnHeaderText: $tm('general.devicetype'),
                minWidth: 170,
                maxWidth: 170,
                editable: false,
                sortable: true,
                resizable: false,
                cellRenderer: ValidatedText,
                cellRendererParams: { translationprefix: 'devicetype', routename: null },
            }),
        );

        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'DeviceStatus.OnBattery',
                cellDataType: 'date',
                cellRenderer: BatteryStatus,
                columnHeaderText: $tm('general.batterystatus'),
                editable: false,
                minWidth: 90,
                sortable: false,
                maxWidth: 90,
            }),
        );

        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'DeviceStatus.Antennas',
                cellRenderer: AntennaStatus,
                columnHeaderText: $tm('general.antennastatus'),
                editable: false,
                sortable: false,
                minWidth: 140,
                maxWidth: 140,
            }),
        );
    }
    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'DeviceStatus.LastSeen',
            cellDataType: 'date',
            cellRenderer: DateComp,
            cellRendererParams: { format: 'DATETIME', monospace: false },
            columnHeaderText: $tm('general.lastseen'),
            minWidth: 180,
            maxWidth: 180,
            editable: false,
            cellClassRules: [
                {
                    className: 'grid-cell-success',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 5 * 60 * 1000; // 5 minutes
                        let diff =
                            row.DeviceStatus?.LastSeen != null ? new Date().getTime() - row.DeviceStatus?.LastSeen.getTime() : 999999;

                        return diff <= limit;
                    },
                },
                {
                    className: 'grid-cell-info',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let sixMonthsMs = 15768000000; //(60*60*24*365*1000*0.5)
                        return (
                            row.DeviceStatus?.LastSeen != null && row.DeviceStatus?.LastSeen < new Date(new Date().getTime() - sixMonthsMs)
                        );
                    },
                },
                {
                    className: 'grid-cell-warning',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 5 * 60 * 1000; // 5 minutes
                        let diff = row.DeviceStatus?.LastSeen != null ? new Date().getTime() - row.DeviceStatus?.LastSeen.getTime() : 0;

                        return diff >= limit && diff < 15 * 60 * 1000;
                    },
                },
                {
                    className: 'grid-cell-error',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 15 * 60 * 1000; // 15 minutes
                        let diff = row.DeviceStatus?.LastSeen != null ? new Date().getTime() - row.DeviceStatus?.LastSeen.getTime() : 0;
                        return diff >= limit && diff < 100 * limit;
                    },
                },
            ],
        }),
    );

    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'DeviceStatus.ReceiveOffset',
            cellDataType: 'timespan',
            cellRenderer: DeviceReceiveOffset,
            columnHeaderText: $tm('general.receiveoffset'),
            minWidth: 110,
            maxWidth: 110,
            editable: false,
            cellClassRules: [
                {
                    className: 'grid-cell-success',
                    func: (cell: TimeSpan, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 8 * 1000; // 8 seconds
                        return Math.abs(cell?.TotalMilliseconds ?? 999999999) <= limit;
                    },
                },
                {
                    className: 'grid-cell-warning',
                    func: (cell: TimeSpan, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 8 * 1000; // 8 seconds
                        return Math.abs(cell?.TotalMilliseconds ?? 0) >= limit;
                    },
                },
                {
                    className: 'grid-cell-error',
                    func: (cell: TimeSpan, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 2 * 60 * 1000; // 2 minutes
                        return Math.abs(cell?.TotalMilliseconds ?? 0) >= limit;
                    },
                },
            ],
        }),
    );

    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'DeviceStatus.LastTag',
            cellDataType: 'number',
            cellRenderer: ValidatedText,
            columnHeaderText: $tm('devicestatus.lasttag'),
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            editable: false,
        }),
    );

    _columns.push(
        new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
            dataField: 'DeviceStatus.LastTagTime',
            cellDataType: 'date',
            cellRenderer: DateComp,
            cellRendererParams: { format: 'DATETIME', monospace: false },
            columnHeaderText: $tm('general.lasttagtime'),
            minWidth: 180,
            maxWidth: 180,
            cellClassRules: [
                {
                    className: 'grid-cell-success',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 5 * 60 * 1000; // 5 minutes
                        let diff =
                            row.DeviceStatus?.LastTagTime != null ? new Date().getTime() - row.DeviceStatus?.LastTagTime.getTime() : 999999;

                        return diff <= limit;
                    },
                },
                {
                    className: 'grid-cell-warning',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 5 * 60 * 1000; // 5 minutes
                        let diff =
                            row.DeviceStatus?.LastTagTime != null ? new Date().getTime() - row.DeviceStatus?.LastTagTime.getTime() : 0;

                        return diff >= limit && diff < 15 * 60 * 1000;
                    },
                },
                {
                    className: 'grid-cell-error',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        let limit = 15 * 60 * 1000; // 15 minutes
                        let diff = row.DeviceStatus?.LastSeen != null ? new Date().getTime() - row.DeviceStatus?.LastSeen.getTime() : 0;
                        return diff >= limit && diff < 24 * 60 * 60 * 1000;
                    },
                },
                {
                    className: 'grid-cell-error',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        // This rule will be true if the device has not been seen for 5 minutes after the last tag
                        return (
                            row.DeviceStatus?.LastSeen != null &&
                            row.DeviceStatus?.LastTagTime != null &&
                            row.DeviceStatus?.LastTagTime?.getTime() > 0 &&
                            row.DeviceStatus?.LastSeen?.getTime() > 0 &&
                            Math.abs(row.DeviceStatus?.LastSeen?.getTime() - row.DeviceStatus?.LastTagTime?.getTime()) > 5 * 60 * 1000
                        );
                    },
                },
                {
                    className: 'grid-cell-info',
                    func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                        // We ignore the rule if the last tag time is not set
                        if (row.DeviceStatus?.LastTagTime?.getFullYear() == 1) {
                            return false;
                        }
                        let today = new Date().toISOString().substring(0, 10).replace(/-/gi, '');
                        let lastTagDay = row.DeviceStatus?.LastTagTime?.toISOString().substring(0, 10).replace(/-/gi, '');

                        if (lastTagDay == undefined) {
                            return false;
                        }

                        let limit = 24 * 60 * 60 * 1000; // 24 hours
                        let diff =
                            row.DeviceStatus?.LastTagTime != null ? new Date().getTime() - row.DeviceStatus?.LastTagTime.getTime() : 0;

                        return diff <= limit && today != lastTagDay;
                    },
                },
            ],
            editable: false,
        }),
    );

    if (props.dataType == 'StationDeviceStatus') {
        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'DeviceStatus.Messages',
                cellDataType: 'string',
                cellRenderer: DeviceStatusMessages,
                columnHeaderText: $tm('devicestatus.messages'),
                editable: false,
                cellClassRules: [
                    {
                        className: 'grid-cell-error',
                        func: (cell: Date, row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                            return row.DeviceStatus?.HasMessageAlert == true;
                        },
                    },
                ],
            }),
        );

        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'StationAlias.ActiveFrom',
                cellDataType: 'text',
                sortable: true,
                cellRenderer: DateComp,
                valueGetter: (row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                    return row.StationAlias?.ID == 0 ? null : row.StationAlias!.ActiveFrom;
                },
                cellRendererParams: { format: 'DATETIME', monospace: false },
                columnHeaderText: $tm('general.activefrom'),
                minWidth: 180,
                maxWidth: 180,
                editable: false,
            }),
        );

        _columns.push(
            new GridColumnOptions<StationDeviceStatus | StationDeviceStatusWithCount>({
                dataField: 'StationAlias.ActiveTo',
                cellDataType: 'text',
                cellRenderer: DateComp,
                valueGetter: (row: StationDeviceStatus | StationDeviceStatusWithCount) => {
                    return row.StationAlias?.ID == 0 ? null : row.StationAlias!.ActiveTo;
                },
                sortable: true,
                cellRendererParams: { format: 'DATETIME', monospace: false },
                columnHeaderText: $tm('general.activeto'),
                minWidth: 180,
                maxWidth: 180,
                editable: false,
            }),
        );
    }

    return _columns;
});

/**
 * Define the datasource for the grid
 */
const dataSource = new GridDataSource<any>({
    rowGetter: (p: IGridRowGetterParams<any>) => {
        p?.sort(props.rows);

        const slice = props.rows.slice(p.startRow, p.endRow);

        return p.successCallback(slice, props.rows, props.rows.length);
    },
});

/**
 * Define the grid setup
 */
const gridSetup = new GridSetup<StationDeviceStatus>({
    rowIdGetter: (row: StationDeviceStatus) =>
        `${row.StationAlias!.ID!}-${row.StationAlias!.AliasWithoutEtsChannel!}-${row.Station?.ID!}-${row.StationAlias?.ActiveFrom?.getTime()}-${row.StationAlias?.ActiveTo?.getTime()}`,
    datasource: dataSource,
    rowSelection: 'multiple',
    GridColumnDefinition: new GridColumnDefinition({ ColumnOptions: columns.value }),
    rowClassRules: [
        {
            className: 'grid-row-muted',
            func: (data: StationDeviceStatus) => data?.DeviceStatus?.LastSeen?.getDate() != new Date().getDate(),
        },
    ],
});

/**
 * Set up an interval to refresh all he last seen cells in all the rows every 3 minutes
 */
setInterval(
    () => {
        grid?.value?.refreshCells(undefined, ['DeviceStatus.LastSeen', 'DeviceStatus.LastTagTime']);
    },
    3 * 60 * 1000,
);

defineExpose({
    UpdateDeviceStatusGrid: (deviceIds: string[]) => {
        console.log('we try and refresh the grid', deviceIds);
        grid?.value?.refreshCachedData();
    },
});
</script>
